<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          User Documents - {{ user.user_name || "Loading..." }}
        </v-card-title>
        <v-card-text>
          <!-- Search and Filter Row -->
          <v-row class="mb-4">
            <v-col sm="6">
              <v-text-field
                v-model="searchKeyword"
                outlined
                solo
                label="Search documents..."
                prepend-inner-icon="mdi-magnify"
                @input="searchDocuments"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-select
                v-model="statusFilter"
                :items="statusItems"
                outlined
                solo
                label="Filter by Status"
                @change="getData"
              ></v-select>
            </v-col>
            <v-col sm="3">
              <v-btn
                color="primary"
                @click="showAddDialog = true"
                :disabled="!canAddDocument"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Document
              </v-btn>
            </v-col>
          </v-row>

          <!-- Documents Data Table -->
          <v-data-table
            :headers="headers"
            :items="documents"
            :loading="isLoading"
            class="elevation-1"
            fixed-header
            height="400"
          >
            <!-- Document Type Column -->
            <template v-slot:[`item.doc_type_name`]="{ item }">
              <v-chip small outlined color="primary">
                {{ getDocumentTypeName(item.doc_type_id) }}
              </v-chip>
            </template>

            <!-- File Column -->
            <template v-slot:[`item.file_info`]="{ item }">
              <div v-if="item.file_path">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="viewDocument(item)"
                  title="View Document"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="info"
                  @click="downloadDocument(item)"
                  title="Download Document"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <br />
                <small class="text--secondary">{{
                  item.file_size || "Unknown size"
                }}</small>
              </div>
              <small v-else class="text--secondary">No file</small>
            </template>

            <!-- Status Column -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                small
                :color="getStatusColor(item.status)"
                text-color="white"
              >
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <!-- Verified Info Column -->
            <template v-slot:[`item.verified_info`]="{ item }">
              <div v-if="item.status === 1">
                <small>
                  <strong>By:</strong> {{ item.verified_by_name || "N/A"
                  }}<br />
                  <strong>At:</strong> {{ formatDate(item.verified_at) }}
                </small>
              </div>
              <small v-else class="text--secondary">Not verified</small>
            </template>

            <!-- Actions Column -->
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex">
                <!-- Edit Button (only for pending/rejected) -->
                <v-btn
                  v-if="item.status === 0 || item.status === 2"
                  icon
                  small
                  color="warning"
                  @click="editDocument(item)"
                  title="Edit Document"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>

                <!-- Verify Button (Admin only) -->
                <v-btn
                  v-if="item.status === 0 && canVerifyDocuments"
                  icon
                  small
                  color="success"
                  @click="verifyDocument(item, true)"
                  title="Approve Document"
                >
                  <v-icon>mdi-check</v-icon>
                </v-btn>

                <!-- Reject Button (Admin only) -->
                <v-btn
                  v-if="item.status === 0 && canVerifyDocuments"
                  icon
                  small
                  color="error"
                  @click="verifyDocument(item, false)"
                  title="Reject Document"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>

                <!-- Delete Button (only for pending/rejected) -->
                <v-btn
                  v-if="item.status === 0 || item.status === 2"
                  icon
                  small
                  color="error"
                  @click="deleteDocument(item)"
                  title="Delete Document"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add Document Dialog -->
      <v-dialog v-model="showAddDialog" max-width="600px">
        <v-card>
          <v-card-title>Add New Document</v-card-title>
          <v-card-text>
            <v-form ref="addForm" v-model="addFormValid">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="addForm.doc_type_id"
                    :items="documentTypes"
                    outlined
                    label="Document Type *"
                    prepend-inner-icon="mdi-file-document"
                    :error-messages="getError('doc_type_id')"
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-file-input
                    v-model="addForm.file"
                    outlined
                    label="Upload Document *"
                    prepend-inner-icon="mdi-upload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    :error-messages="getError('file')"
                    :rules="[rules.required]"
                  ></v-file-input>
                  <small class="text--secondary">
                    Supported formats: PDF, JPG, JPEG, PNG, DOC, DOCX (Max:
                    10MB)
                  </small>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeAddDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="isSaving"
              :disabled="!addFormValid"
              @click="saveDocument"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Document Dialog -->
      <v-dialog v-model="showEditDialog" max-width="600px">
        <v-card>
          <v-card-title>Edit Document</v-card-title>
          <v-card-text>
            <v-form ref="editForm" v-model="editFormValid">
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="editForm.doc_type_id"
                    :items="documentTypes"
                    outlined
                    label="Document Type *"
                    prepend-inner-icon="mdi-file-document"
                    :error-messages="getError('doc_type_id')"
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-file-input
                    v-model="editForm.new_file"
                    outlined
                    label="Update Document (Optional)"
                    prepend-inner-icon="mdi-upload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    :error-messages="getError('file')"
                  ></v-file-input>
                  <small class="text--secondary">
                    Leave empty to keep current file. Current:
                    {{ editForm.file_name || editForm.file_path }}
                  </small>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeEditDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="isSaving"
              :disabled="!editFormValid"
              @click="updateDocument"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500px">
        <v-card>
          <v-card-title>
            <v-icon large color="error">mdi-alert-circle</v-icon>
            &nbsp; Confirm Delete
          </v-card-title>
          <v-card-text>
            Are you sure you want to delete this document? This action cannot be
            undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showDeleteDialog = false"
              >Cancel</v-btn
            >
            <v-btn color="error" :loading="isDeleting" @click="confirmDelete">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <FileViewer v-model="fileViewerDialog" :url="fileViewerUrl" />
    </v-container>
  </v-main>
</template>

<script>
// import mixin from "@/mixin";
import axios from "axios";
import { FileViewer } from "@/components";

export default {
  name: "UserDocuments",
  components: { FileViewer },
  // mixins: [mixin],
  data() {
    return {
      user: {},
      documents: [],
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      searchKeyword: "",
      statusFilter: "",

      // Dialog states
      showAddDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      fileViewerDialog: false,
      fileViewerUrl: '',

      // Form validation
      addFormValid: false,
      editFormValid: false,

      // Form data
      addForm: {
        doc_type_id: null,
        file: null,
      },
      editForm: {
        id: null,
        doc_type_id: null,
        new_file: null,
        file_name: "",
        file_path: "",
      },

      // Document to be deleted
      documentToDelete: null,

      // Document types
      documentTypes: [
        { value: 1, text: "Passport" },
        { value: 2, text: "Driving License" },
        { value: 3, text: "National ID" },
        { value: 4, text: "Birth Certificate" },
        { value: 5, text: "Utility Bill" },
        { value: 6, text: "Bank Statement" },
        { value: 7, text: "Medical Certificate" },
        { value: 8, text: "Employment Letter" },
        { value: 9, text: "Tax Document" },
        { value: 10, text: "Insurance Policy" },
      ],

      // Status items for filter
      statusItems: [
        { value: "", text: "All Status" },
        { value: "0", text: "Pending" },
        { value: "1", text: "Approved" },
        { value: "2", text: "Rejected" },
      ],

      // Data table headers
      headers: [
        { text: "Document Type", value: "doc_type_name", sortable: false },
        { text: "File", value: "file_info", sortable: false },
        { text: "Status", value: "status", sortable: true },
        { text: "Verified Info", value: "verified_info", sortable: false },
        { text: "Created At", value: "created_at", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],

      // Validation rules
      rules: {
        required: (value) => !!value || "This field is required",
      },

      errors: {},
    };
  },

  computed: {
    userId() {
      return this.$route.params.userId;
    },

    canAddDocument() {
      return this.documents.length < 10;
    },

    canVerifyDocuments() {
      return this.roleName === "ADMIN" || this.roleName === "SUPER ADMIN";
    },
  },

  mounted() {
    this.getUserInfo();
    this.getData();
  },

  methods: {
    async getUserInfo() {
      try {
        const response = await axios.get(`/users/${this.userId}`);
        this.user = response.data.data;
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    },

    async getData() {
      try {
        this.isLoading = true;
        const params = {
          user_id: this.decryptIt(this.userId),
        };

        if (this.searchKeyword) {
          params.search_keyword = this.searchKeyword;
        }

        if (this.statusFilter !== "") {
          params.status = this.statusFilter;
        }

        const response = await axios.get("/user_documents", { params });
        this.documents = response.data.data;
      } catch (error) {
        console.error("Error fetching documents:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      } finally {
        this.isLoading = false;
      }
    },

    searchDocuments() {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getData();
      }, 500);
    },

    async saveDocument() {
      try {
        this.isSaving = true;
        this.errors = {};

        const formData = new FormData();
        formData.append("user_id", this.decryptIt(this.userId));
        formData.append("doc_type_id", this.addForm.doc_type_id);
        formData.append("file", this.addForm.file);

        await axios.post("/user_documents", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.closeAddDialog();
        this.getData();
        this.$toast.success("Document added successfully");
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$toast.error("Error adding document");
        }
      } finally {
        this.isSaving = false;
      }
    },

    async updateDocument() {
      try {
        this.isSaving = true;
        this.errors = {};

        const formData = new FormData();
        formData.append("user_id", this.decryptIt(this.userId));
        formData.append("doc_type_id", this.editForm.doc_type_id);

        if (this.editForm.new_file) {
          formData.append("file", this.editForm.new_file);
        }

        await axios.patch(
          `/user_documents/${this.encryptIt(this.editForm.id)}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.closeEditDialog();
        this.getData();
        this.$toast.success("Document updated successfully");
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$toast.error("Error updating document");
        }
      } finally {
        this.isSaving = false;
      }
    },

    async confirmDelete() {
      try {
        this.isDeleting = true;
        await axios.post(
          `/user_documents/delete/${this.encryptIt(this.documentToDelete.id)}`
        );
        this.showDeleteDialog = false;
        this.getData();
        this.$toast.success("Document deleted successfully");
      } catch (error) {
        this.$toast.error("Error deleting document");
      } finally {
        this.isDeleting = false;
      }
    },

    async verifyDocument(document, approve) {
      try {
        const status = approve ? 1 : 2; // 1 = approved, 2 = rejected
        await axios.patch(`/user_documents/${this.encryptIt(document.id)}`, {
          status: status,
          user_id: this.decryptIt(this.userId),
          doc_type_id: document.doc_type_id,
        });

        this.getData();
        this.$toast.success(
          `Document ${approve ? "approved" : "rejected"} successfully`
        );
      } catch (error) {
        this.$toast.error(
          `Error ${approve ? "approving" : "rejecting"} document`
        );
      }
    },

    editDocument(document) {
      this.editForm = {
        id: document.id,
        doc_type_id: document.doc_type_id,
        new_file: null,
        file_name: document.file_name || document.file_path,
        file_path: document.file_path,
      };
      this.showEditDialog = true;
    },

    deleteDocument(document) {
      this.documentToDelete = document;
      this.showDeleteDialog = true;
    },

    viewDocument(document) {
      if (document.file_path) {
        const baseUrl = this.$store.getters["settings/getMediaUrl"];
        this.fileViewerUrl = `${baseUrl}${document.file_path}`;
        this.fileViewerDialog = true;
      }
    },

    downloadDocument(document) {
      if (document.file_path) {
        const baseUrl = this.$store.getters["settings/getMediaUrl"];
        const documentUrl = `${baseUrl}${document.file_path}`;
        const link = document.createElement("a");
        link.href = documentUrl;
        link.download = document.file_name || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    closeAddDialog() {
      this.showAddDialog = false;
      this.addForm = {
        doc_type_id: null,
        file: null,
      };
      this.errors = {};
      if (this.$refs.addForm) {
        this.$refs.addForm.resetValidation();
      }
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.editForm = {
        id: null,
        doc_type_id: null,
        new_file: null,
        file_name: "",
        file_path: "",
      };
      this.errors = {};
      if (this.$refs.editForm) {
        this.$refs.editForm.resetValidation();
      }
    },

    getDocumentTypeName(docTypeId) {
      const docType = this.documentTypes.find(
        (type) => type.value === docTypeId
      );
      return docType ? docType.text : "Unknown";
    },

    getStatusColor(status) {
      switch (Number(status)) {
        case 0:
          return "orange"; // Pending
        case 1:
          return "green"; // Approved
        case 2:
          return "red"; // Rejected
        default:
          return "grey";
      }
    },

    getStatusText(status) {
      switch (Number(status)) {
        case 0:
          return "Pending";
        case 1:
          return "Approved";
        case 2:
          return "Rejected";
        default:
          return "Unknown";
      }
    },

    getError(field) {
      return this.errors[field] || [];
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return "Invalid Date";
      }
    },
  },
};
</script>

<style scoped>
.elevation-1 {
  border-radius: 8px;
}
</style>